import { useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import CartaoDia from "./components/CartaoDia";
import Desempenho from "./components/Desempenho";
import GradeSemana from "./components/GradeSemana";
import { DIAS, ORDEM } from "./lib/horarios";
import useHorarios from "./useHorarios";

const Carregando = () => (
  <div className="grid gap-2 rounded-2xl bg-muted/60 p-2 lg:grid-cols-[270px_minmax(0,1fr)]" data-e2e="horarios-carregando">
    <div className="flex flex-col gap-2">{ORDEM.map((dw) => <Skeleton key={dw} className="h-[62px] rounded-xl" />)}</div>
    <Skeleton className="h-[600px] rounded-xl" />
  </div>
);

/**
 * Configurações → Horários (repaginada em 25/10/2026, a partir do modelo do
 * dono): à esquerda os 7 dias em cards (clicar edita), à direita a semana em
 * grade com os picos de almoço e jantar, e embaixo o Desempenho.
 */
export default function HorariosTab() {
  const h = useHorarios();
  const [editando, setEditando] = useState(() => new Set());
  const [copiando, setCopiando] = useState(null);

  const editar = (dw) => setEditando((s) => new Set(s).add(dw));
  const concluir = (dw) => {
    setEditando((s) => { const n = new Set(s); n.delete(dw); return n; });
    if (copiando === dw) setCopiando(null);
  };
  const copiar = (de, para) => {
    h.copiarDia(de, para);
    setCopiando(null);
    toast.success(`Horário de ${DIAS[de].toLowerCase()} copiado para ${para.length} ${para.length > 1 ? "dias" : "dia"}. Clique em Salvar para confirmar.`);
  };
  const salvar = async () => { if (await h.salvar()) { setEditando(new Set()); setCopiando(null); } };
  const descartar = () => {
    h.descartar(); setEditando(new Set()); setCopiando(null);
    toast("Alterações descartadas. Os horários voltaram ao último salvo.");
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1220px] mx-auto space-y-4" data-e2e="tela-horarios">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2.5"><Clock className="w-7 h-7 text-primary" />Horários de Funcionamento</h1>
        <p className="text-sm text-muted-foreground mt-1">Quando a loja atende. Vale para o cardápio digital, o totem, o bot e o iFood. Clique num dia para mudar.</p>
      </div>

      {h.carregando ? <Carregando /> : h.erroCarga ? (
        <div className="rounded-2xl border p-6 text-center text-sm text-muted-foreground">
          Não deu para carregar os horários. <button type="button" className="text-primary font-semibold hover:underline" onClick={h.carregar}>Tentar de novo</button>
        </div>
      ) : (
        <>
          <div className="grid gap-2 rounded-2xl bg-muted/60 p-2 lg:grid-cols-[270px_minmax(0,1fr)]">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2 flex-1">
                {ORDEM.map((dw) => {
                  const dia = h.rascunho.find((d) => d.dw === dw);
                  return (
                    <CartaoDia key={dw} dia={dia} editando={editando.has(dw)}
                      onEditar={() => editar(dw)} onConcluir={() => concluir(dw)}
                      mudar={(fn) => h.mudarDia(dw, fn)}
                      copiando={copiando === dw} onAbrirCopia={() => setCopiando(dw)} onFecharCopia={() => setCopiando(null)}
                      onCopiar={(para) => copiar(dw, para)} />
                  );
                })}
              </div>
              {(h.alterado || editando.size > 0) && (
                <div className="flex gap-2">
                  {h.alterado && (
                    <Button variant="outline" className="flex-1 h-11 font-semibold" onClick={descartar} disabled={h.salvando} data-e2e="descartar-horarios">Descartar</Button>
                  )}
                  <Button className="flex-1 h-11 font-semibold" onClick={salvar} disabled={!h.alterado || h.comErro || h.salvando} data-e2e="salvar-horarios"
                    title={h.comErro ? "Tem horário que fecha antes de abrir" : ""}>
                    {h.salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salvar
                  </Button>
                </div>
              )}
            </div>
            <GradeSemana dias={h.rascunho} editando={editando} onEditar={editar} />
          </div>
          <Desempenho dias={h.rascunho} />
        </>
      )}
    </div>
  );
}
